import { Box, Stack, TextField } from '@mui/material';
import { ICaseDetailsInputs } from 'API';
import { RadioButtons, TextareaCounter } from 'Components';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary } from 'Hooks';
import { IndexContext } from 'Modules/CaseDetails/CaseContext';
import { useContext, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { generateError } from 'Regex';
import { validateLettersAndNumbersOnly } from 'Utils/validateLettersAndNumbersOnly';
import { validateLettersNumbersAndSpacialChars } from 'Utils/validateLettersNumbersAndSpacialChars';

export const FamilyDiagnosis = () => {
  const { index } = useContext(IndexContext);
  const {
    register,
    formState: { errors },
  } = useFormContext<ICaseDetailsInputs>();

  const [
    textAreaRelativesWithSameDisease,
    setTextAreaRelativesWithSameDisease,
  ] = useState<string>('');

  const dictionary = useDictionary(Dictionary, DICTIONARY.CASE_DETAILS);

  const baseName = `caseDetails.${index}.diseaseKindSection`;

  const [isRelativesWithSameDisease, relativesWithSameDisease] = useWatch({
    name: [
      `${baseName}.isRelativesWithSameDisease`,
      `${baseName}.relativesWithSameDisease`,
    ],
  });

  const diseaseKindSectionServiceError = errors.caseDetails
    ? errors.caseDetails[index]?.diseaseKindSection
    : undefined;

  useEffect(() => {
    if (relativesWithSameDisease)
      setTextAreaRelativesWithSameDisease(relativesWithSameDisease);
  }, [relativesWithSameDisease]);

  return (
    <>
      <RadioButtons
        id={
          dictionary.secondSectionIsThereAnyOtherFamilyMemberThatSuffersFromThatDisease
        }
        name={`${baseName}.isRelativesWithSameDisease`}
        options={[
          {
            value: 'true',
            label: dictionary.secondSectionFamilyInjuryYes,
          },
          {
            value: 'false',
            label: dictionary.secondSectionFamilyInjuryNo,
          },
        ]}
      />
      {isRelativesWithSameDisease && (
        <Stack gap='20px' sx={{ mt: '10px' }}>
          <TextField
            id={`${dictionary.secondSectionDiseaseDescFamilyRelation}-${index}`}
            label={dictionary.secondSectionDiseaseDescFamilyRelation}
            inputProps={{
              maxLength: '50',
            }}
            {...register(
              `${baseName}.relativeRelation` as `caseDetails.0.diseaseKindSection.relativeRelation`
            )}
            onChange={(event: any) => validateLettersAndNumbersOnly(event)}
            role={
              Boolean(diseaseKindSectionServiceError?.relativeRelation)
                ? 'alert'
                : undefined
            }
            error={Boolean(diseaseKindSectionServiceError?.relativeRelation)}
            helperText={
              Boolean(
                diseaseKindSectionServiceError?.relativeRelation?.message
              ) &&
              generateError(
                dictionary.secondSectionDiseaseDescFamilyRelation,
                diseaseKindSectionServiceError?.relativeRelation?.message!
              )
            }
          />
          <Box
            sx={{
              position: 'relative',
            }}
          >
            <TextField
              id={`${dictionary.secondSectionDiseaseDescriptionNoJustMe}-${index}`}
              label={dictionary.secondSectionDiseaseDescriptionNoJustMe}
              sx={{ mt: '20px' }}
              inputProps={{
                maxLength: '500',
              }}
              {...register(
                `${baseName}.relativesWithSameDisease` as `caseDetails.0.diseaseKindSection.relativesWithSameDisease`
              )}
              multiline
              rows={6}
              onChange={(event: any) => {
                setTextAreaRelativesWithSameDisease(event.target.value);
                validateLettersNumbersAndSpacialChars(event);
              }}
              role={
                Boolean(
                  diseaseKindSectionServiceError?.relativesWithSameDisease
                )
                  ? 'alert'
                  : undefined
              }
              error={Boolean(
                diseaseKindSectionServiceError?.relativesWithSameDisease
              )}
              helperText={
                Boolean(
                  diseaseKindSectionServiceError?.relativesWithSameDisease
                    ?.message
                ) &&
                generateError(
                  dictionary.secondSectionDiseaseDescriptionNoJustMe,
                  diseaseKindSectionServiceError?.relativesWithSameDisease
                    ?.message!
                )
              }
            />
            <TextareaCounter
              counter={textAreaRelativesWithSameDisease.length}
              limit={500}
              hasError={Boolean(
                diseaseKindSectionServiceError?.relativesWithSameDisease
                  ?.message
              )}
            />
          </Box>
        </Stack>
      )}
    </>
  );
};
